<script setup lang="ts">

import { onClickOutside } from '@vueuse/core'
import {Loader2Icon} from "lucide-vue-next";
import {computed, type Ref, ref} from "vue";
import {label} from "../../mixins/translations";
import {useSearchStore} from "../../stores/search";

interface Props {
    types?: string[],
}

const props = withDefaults<Props>(defineProps<Props>(), {
    types: []
});

const searchStore = useSearchStore();

const selectedOption : Ref<string> = ref('all');
const searchQuery : Ref<string> = ref('');
const openResults : Ref<boolean> = ref(false);

const searchUrl = computed((): string => {
    let url = '/search/' + searchQuery.value.toLowerCase() + '/';
    if (selectedOption.value !== 'all') {
        url = url + selectedOption.value.toLowerCase();
    }
    return url;
})

const navigate = () => {
    if(searchQuery.value.length) {
        window.location.href = searchUrl.value;
    }
}

const preview = async () => {
    try {
        await searchStore.getSearchResults(searchQuery.value, selectedOption.value);
        openResults.value = true;
    } catch {
        // canceled or failed, ignore
    }
}

const target = ref(null);
onClickOutside(target, event => openResults.value = false)
</script>

<template>
    <div class="relative" id="mainMenuSearch">
        <input type="text"
               :placeholder="label('site.search.placeholder')"
               class="border-0 ring-0 h-14 w-full rounded-full bg-gray-100 pl-7 pr-12 text-base text-black placeholder:text-black/30 focus:outline-none"
               v-model="searchQuery" @keyup.enter="navigate" @keyup="preview" />
        <button type="button" class="absolute right-4 top-1/2 -translate-y-1/2 text-black" aria-label="Zoeken">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-search-icon lucide-search h-5 w-5">
                <title>Search</title>
                <path d="m21 21-4.34-4.34"></path>
                <circle cx="11" cy="11" r="8"></circle>
            </svg>
        </button>

        <div class="w-full absolute top-16 bg-gray-50 shadow-xl rounded-xl" v-show="openResults" ref="target">
            <div v-for="(results, key) in searchStore.results">
                <div v-if="results.count">
                    <div v-if="key === 'articles'" class="w-full py-4 px-6 border-t border-gray-200 bg-gray-100">
                        <h3 class="w-full text-lg font-bold tracking-tight text-gray-900" v-text="label('site.search.'+key)"></h3>
                        <div v-for="result in results.items">
                            <a :href="result.full_slug" class="w-full flex bg-white rounded-xl overflow-hidden mt-3" aria-label="Link to article">
                                <div class="w-1/4 h-auto bg-cover bg-center" :style="`background-image: url('${result.image}')`">
                                </div>
                                <div class="w-3/4 p-4">
                                    <h3 class="w-full text-lg font-bold tracking-tight text-gray-900" v-text="result.title"></h3>
                                    <p class="w-full mt-1 font-light text-sm" v-html="result.content_short"></p>
                                </div>
                            </a>
                        </div>
                    </div>
                    <div v-else-if="key === 'products'" class="w-full py-4 px-6 border-t border-gray-200 bg-gray-100">
                        <h3 class="w-full text-lg font-bold tracking-tight text-gray-900" v-text="label('site.search.'+key)"></h3>
                        <div v-for="result in results.items">
                            <a :href="result.url" class="w-full flex bg-white rounded-xl overflow-hidden mt-3" aria-label="Link to product">
                                <div class="w-1/4 h-auto bg-cover bg-center" :style="`background-image: url('${result.thumbnail.image_url}')`">

                                </div>
                                <div class="w-3/4 p-4">
                                    <h3 class="w-full text-lg font-bold tracking-tight text-gray-900" v-text="result.name"></h3>
                                </div>
                            </a>
                        </div>
                    </div>
                    <div v-else class="w-full py-4 px-6">
                        <h3 class="w-full text-lg font-bold tracking-tight text-gray-900" v-text="label('site.search.'+key)"></h3>
                        <nav class="flex flex-col mt-2 space-y-2 text-sm text-gray-500">
                            <div v-for="result in results.items">
                                <a class="hover:text-black" :href="result.full_slug" v-text="result.title" aria-label="Link to page"></a>
                            </div>
                        </nav>
                    </div>
                </div>
            </div>
            <div class="w-full py-4 px-6 flex" v-if="searchStore.totalResults">
                <a :href="searchUrl" class="px-6 py-3 w-full text-white text-center text-base font-bold bg-slate-700 hover:bg-slate-800 rounded-full focus:bg-slate-800 focus:outline-hidden" aria-label="Link to all results" v-html="label('site.search.show-all-results', {'count': searchStore.totalResults})"></a>
            </div>
            <div v-else class="w-full py-4 px-6 flex">
                <div v-text="label('site.search.no-results')"></div>
            </div>

            <div class="h-1 bg-gradient-to-r from-secondary-200 to-primary"></div>
        </div>
    </div>
</template>
